/**
 * AuthorityService - Handles SplTrader authority management
 */
import axios from 'axios';
import authService from './AuthService';

class AuthorityService {
  constructor() {
    this.splinterlandsApi = 'https://api2.splinterlands.com';
    this.targetAccounts = [
      'xiangyang200', 'xiangyang201', 'xiangyang202', 'xiangyang203', 'xiangyang204',
      'xiangyang205', 'xiangyang206', 'xiangyang207', 'xiangyang208', 'xiangyang209',
      'xiangyang210', 'xiangyang211', 'xiangyang212', 'xiangyang213', 'xiangyang214',
      'xiangyang215', 'xiangyang216', 'xiangyang217', 'xiangyang218', 'xiangyang219'
    ];
  }

  /**
   * Check if user has given SplTrader purchase authority (simplified)
   * @param {string} username - The username to check (optional, uses current user if not provided)
   * @returns {Promise<Object>} Authority status (simplified)
   */
  async checkAuthorityStatus(username = null) {
    try {
      const user = username || authService.getCurrentUser()?.username;
      if (!user) {
        throw new Error('No username provided');
      }
      const response = await axios.get(`${this.splinterlandsApi}/players/authorities`, {
        params: { players: user },
        timeout: 10000
      });

      // The API returns an array of player objects
      const playerData = Array.isArray(response.data) 
        ? response.data.find(player => player.name === user)
        : response.data?.[user];

      if (!playerData) {
        return {
          hasAuthority: false,
          status: 'no_authority'
        };
      }

      // Extract purchase authorities from the correct structure
      const authorizedAccounts = playerData.authorities?.purchase || playerData.purchase || [];
      
      // Check if user has authorized any of our target accounts (simplified check)
      const hasAnyAuthority = this.targetAccounts.some(account => 
        authorizedAccounts.includes(account)
      );

      return {
        hasAuthority: hasAnyAuthority,
        status: hasAnyAuthority ? 'authorized' : 'no_authority'
      };

    } catch (error) {
      throw new Error(`Failed to check authority status: ${error.message}`);
    }
  }

  /**
   * Grant SplTrader purchase authority via Hive Keychain
   * @param {string} username - The username (optional, uses current user if not provided)
   * @returns {Promise<Object>} Transaction result
   */
  async grantAuthority(username = null) {
    try {
      const user = username || authService.getCurrentUser()?.username;
      if (!user) {
        throw new Error('No username provided');
      }

      // Check if Hive Keychain is available
      if (!window.hive_keychain) {
        throw new Error('Hive Keychain not found. Please install Hive Keychain extension.');
      }

      // Get current authorities to preserve existing accounts
      const currentAuthorities = await this.getCurrentAuthorities(user);
      const existingPurchaseAccounts = currentAuthorities.purchase || [];
      
      // Merge existing accounts with our target accounts (remove duplicates)
      const mergedAccounts = [...new Set([...existingPurchaseAccounts, ...this.targetAccounts])];
      
      // Create the custom JSON operation
      const customJson = {
        id: 'sm_set_authority',
        json: JSON.stringify({
          purchase: mergedAccounts
        }),
        required_auths: [user],
        required_posting_auths: []
      };

      // Execute transaction via Hive Keychain
      return new Promise((resolve, reject) => {
        window.hive_keychain.requestCustomJson(
          user,
          customJson.id,
          'Active',
          customJson.json,
          'Grant Purchase Authority',
          (response) => {
            if (response.success) {
              resolve({
                success: true,
                transactionId: response.result.id,
                message: 'Purchase authority granted successfully'
              });
            } else {
              console.error('Authority grant failed:', response);
              reject(new Error(response.message || 'Failed to grant authority'));
            }
          }
        );
      });

    } catch (error) {
      console.error('Error granting authority:', error);
      throw error;
    }
  }

  /**
   * Revoke SplTrader purchase authority via Hive Keychain
   * @param {string} username - The username (optional, uses current user if not provided)
   * @returns {Promise<Object>} Transaction result
   */
  async revokeAuthority(username = null) {
    try {
      const user = username || authService.getCurrentUser()?.username;
      if (!user) {
        throw new Error('No username provided');
      }

      // Check if Hive Keychain is available
      if (!window.hive_keychain) {
        throw new Error('Hive Keychain not found. Please install Hive Keychain extension.');
      }

      // Get current authorities to preserve existing accounts
      const currentAuthorities = await this.getCurrentAuthorities(user);
      const existingPurchaseAccounts = currentAuthorities.purchase || [];
      
      // Remove only our target accounts, keep other existing accounts
      const filteredAccounts = existingPurchaseAccounts.filter(account => 
        !this.targetAccounts.includes(account)
      );
      
      // Create the custom JSON operation
      const customJson = {
        id: 'sm_set_authority',
        json: JSON.stringify({
          purchase: filteredAccounts
        }),
        required_auths: [user],
        required_posting_auths: []
      };

      // Execute transaction via Hive Keychain
      return new Promise((resolve, reject) => {
        window.hive_keychain.requestCustomJson(
          user,
          customJson.id,
          'Active',
          customJson.json,
          'Revoke Purchase Authority',
          (response) => {
            if (response.success) {
              resolve({
                success: true,
                transactionId: response.result.id,
                message: 'Purchase authority revoked successfully'
              });
            } else {
              console.error('Authority revoke failed:', response);
              reject(new Error(response.message || 'Failed to revoke authority'));
            }
          }
        );
      });

    } catch (error) {
      console.error('Error revoking authority:', error);
      throw error;
    }
  }

  /**
   * Get a user-friendly status message (simplified)
   * @param {Object} authorityStatus - Result from checkAuthorityStatus()
   * @returns {Object} Status message and color
   */
  getStatusMessage(authorityStatus) {
    if (!authorityStatus) {
      return {
        message: 'Authority status unknown',
        color: 'grey',
        severity: 'info'
      };
    }

    switch (authorityStatus.status) {
      case 'authorized':
        return {
          message: 'Purchase access enabled - You can create and manage bids',
          color: 'success',
          severity: 'success'
        };
      case 'no_authority':
        return {
          message: 'Purchase access disabled - Grant authority to create bids',
          color: 'error',
          severity: 'error'
        };
      default:
        return {
          message: 'Authority status unknown',
          color: 'grey',
          severity: 'info'
        };
    }
  }

  /**
   * Check if user can create bids (has necessary authority)
   * @param {string} username - The username (optional, uses current user if not provided)
   * @returns {Promise<boolean>} Whether user can create bids
   */
  async canCreateBids(username = null) {
    try {
      const status = await this.checkAuthorityStatus(username);
      return status.hasAuthority;
    } catch (error) {
      console.error('Error checking bid creation capability:', error);
      return false;
    }
  }

  /**
   * Get current authorities for a user
   * @param {string} username - The username to check
   * @returns {Promise<Object>} Current authorities
   */
  async getCurrentAuthorities(username) {
    try {
      const response = await axios.get(`${this.splinterlandsApi}/players/authorities`, {
        params: { players: username },
        timeout: 10000
      });

      // The API returns an array of player objects
      const playerData = Array.isArray(response.data) 
        ? response.data.find(player => player.name === username)
        : response.data?.[username];

      if (!playerData) {
        return { purchase: [], rental: [] };
      }

      return {
        purchase: playerData.authorities?.purchase || playerData.purchase || [],
        rental: playerData.authorities?.rental || playerData.rental || []
      };

    } catch (error) {
      console.error('Error fetching current authorities:', error);
      // Return empty authorities if fetch fails
      return { purchase: [], rental: [] };
    }
  }
}

// Export singleton instance
const authorityService = new AuthorityService();
export default authorityService;
